import { useTenderDeadline } from "../../../../../hooks/useTenderDeadline";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import moment from "moment";
import cn from "classnames";
import { toast } from "react-toastify";
import style from "./ComplexTenderBids.module.scss";
import RebidNotice from "../../../order-info/components/RebidNotice/RebidNotice";
import { Order } from "../../../../../services";
import { numberToFinanceFormat } from "../../../../../utils";
import { TenderDeadlineText } from "../../../../shared/TenderDeadlineText/TenderDeadlineText";
import { CURRENCIES } from "../../../../../mock";
import CommentIcon from "../../../../shared/icons/CommentIcon";
import ArrowDownIconNew from "../../../../shared/icons/ArrowDownIconNew";
import CustomButton from "../../../../custom/custom-button/CustomButton";
import CloseIconNew from "../../../../shared/icons/CloseIconNew";
import CustomDatePicker from "../../../../shared/date-picker/Date/CustomDatePicker";
import CalendarIcon from "../../../../shared/icons/CalendarIcon";
import ThreeDots from "../../../../shared/Loading/ThreeDots";
import AppTooltip from "../../../../shared/AppTooltip/AppTooltip";
import { useTranslation } from "react-i18next";
import { isRailwayOrder } from "../../../../../pages/orders/utils/railwayOrder";

const EMPTY_VALUE = "-";

const ORDERS_CREATION_DELAY_MS = 3000;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getVehicleWordKey = (count) => {
  const value = Math.abs(Number(count)) % 100;
  const lastDigit = value % 10;
  if (value > 10 && value < 20) return "many";
  if (lastDigit === 1) return "one";
  if (lastDigit >= 2 && lastDigit <= 4) return "few";
  return "many";
};

const formatDate = (date) => {
  if (!date) return EMPTY_VALUE;
  return moment(date).format("DD.MM.YYYY");
};

const formatBidDate = (date) => {
  if (!date) return EMPTY_VALUE;
  return `${moment(date).format("DD.MM.YYYY")} (${moment(date).format("HH:mm")})`;
};

const normalizeInputDate = (date) => {
  if (!date) return "";
  return moment(date).format("YYYY-MM-DD");
};

const dateStringToDate = (date) => {
  if (!date) return null;
  const parsed = moment(date, "YYYY-MM-DD", true);
  return parsed.isValid() ? parsed.toDate() : moment(date).toDate();
};

const buildInitialDates = (count, sourceDates = []) => (
  Array.from({ length: count }, (_, index) => normalizeInputDate(sourceDates[index]?.shippingDate))
);

const DateFields = ({ dates = [], onChangeDate, transportContext }) => {
  const { t } = useTranslation();

  return (
    <div className={style.date_grid}>
      {dates.map((value, index) => (
        <div className={style.date_picker_field} key={index}>
          <span className={style.date_picker_label}>
            {t("complexTender.clientBids.vehicleDateLabel", { number: index + 1, context: transportContext })}
          </span>
          <CustomDatePicker
            selected={dateStringToDate(value)}
            onChange={(date) => onChangeDate(index, date ? normalizeInputDate(date) : "")}
            placeholderText={t("complexTender.clientBids.chooseDate")}
            bgGray
            withBorder
            IconStart={<CalendarIcon />}
            popperProps={{ strategy: "fixed" }}
          />
        </div>
      ))}
    </div>
  );
};

const DateDistributionModal = ({
  isOpen,
  bid,
  count,
  dates,
  onChangeDates,
  onClose,
  transportContext,
}) => {
  const { t } = useTranslation();
  const [draftDates, setDraftDates] = useState([]);

  React.useEffect(() => {
    if (isOpen) {
      setDraftDates(buildInitialDates(count, dates.map((shippingDate) => ({ shippingDate }))));
    }
  }, [isOpen, count, dates]);

  if (!isOpen || !bid) return null;

  const handleDateChange = (index, value) => {
    setDraftDates((prev) => prev.map((item, itemIndex) => (itemIndex === index ? value : item)));
  };

  const handleReady = () => {
    onChangeDates(draftDates);
    onClose();
  };

  return ReactDOM.createPortal(
    <div className={style.modal_overlay} onClick={onClose} data-portal-modal="true">
      <div className={style.date_modal} onClick={(event) => event.stopPropagation()}>
        <div className={style.modal_header}>
          <span className={style.modal_title}>{t("complexTender.clientBids.loadingDate")}</span>
          <button className={style.modal_close} onClick={onClose} aria-label={t("common.close")}>
            <CloseIconNew width="24" height="24" fill="#6D7885" />
          </button>
        </div>

        <div className={style.date_modal_body}>
          <div className={style.date_modal_topbar}>
            <span>
              {t("complexTender.clientBids.vehicleCount", { count, context: transportContext })}: <b>{count}</b>
            </span>
          </div>
          <DateFields dates={draftDates} onChangeDate={handleDateChange} transportContext={transportContext} />
        </div>

        <div className={style.modal_footer}>
          <span className={style.distribution_text}>
            {t("complexTender.clientBids.distributed")}: <b>{t("complexTender.common.countOfTotal", { count, total: count })}</b>
          </span>
          <div className={style.modal_buttons}>
            <CustomButton text={t("common.cancel")} isNavy isWhite onClick={onClose} />
            <CustomButton text={t("complexTender.clientBids.done")} isNavy onClick={handleReady} />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const ComplexTenderBids = ({
  order,
  items = [],
  fetchOrderData,
  onUpdateData,
  onClose,
}) => {
  const { isExpired: isTenderExpiredByServer, isReady: isTenderTimeReady } = useTenderDeadline(order?.tenderDateEnd);
  const { t } = useTranslation();

  const transportContext = isRailwayOrder(order) ? "railway" : undefined;
  const vehicleWord = (count) =>
    t(`complexTender.units.vehicle.${getVehicleWordKey(count)}`, { context: transportContext });

  const [isDistributionMode, setIsDistributionMode] = useState(false);
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [distribution, setDistribution] = useState({});
  const [inlineDateBidId, setInlineDateBidId] = useState(null);
  const [dateModalBidId, setDateModalBidId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isMountedRef = useRef(true);

  useEffect(() => () => { isMountedRef.current = false; }, []);

  const isTenderEnded = order?.tenderDateEnd && isTenderExpiredByServer;
  const isAuction = order?.assignmentMethod === "Auction";
  const titleKey = isAuction ? "complexTender.auctionTitle" : "complexTender.title";
  const totalVehicles = Number(order?.vehiclesCount) || 0;
  const acceptedVehicles = Number(order?.acceptedVehiclesCount) || 0;
  const normalizedItems = useMemo(() => items || [], [items]);
  const allocatedVehicles = useMemo(
    () => Object.values(distribution).reduce(
      (sum, item) => sum + (Number(item?.vehiclesCount) || 0),
      acceptedVehicles,
    ),
    [distribution, acceptedVehicles],
  );

  React.useEffect(() => {
    const activeInlineDistribution = inlineDateBidId ? distribution[inlineDateBidId] : null;
    if (
      inlineDateBidId
      && (!activeInlineDistribution?.vehiclesCount || activeInlineDistribution.vehiclesCount > 9)
    ) {
      setInlineDateBidId(null);
    }
  }, [distribution, inlineDateBidId]);

  const startDistribution = () => {
    const initial = {};
    normalizedItems.forEach((item) => {
      initial[item.id] = {
        vehiclesCount: 0,
        vehiclesDates: [],
      };
    });
    setDistribution(initial);
    setIsDistributionMode(true);
    setActiveCommentId(null);
    setInlineDateBidId(null);
    setDateModalBidId(null);
  };

  const resetDistribution = () => {
    setDistribution({});
    setIsDistributionMode(false);
    setActiveCommentId(null);
    setInlineDateBidId(null);
    setDateModalBidId(null);
  };

  const changeVehiclesCount = (bid, delta) => {
    setDistribution((prev) => {
      const currentItem = prev[bid.id] || { vehiclesCount: 0, vehiclesDates: [] };
      const currentCount = Number(currentItem.vehiclesCount) || 0;
      const availableByBid = Math.max(0, (Number(bid.vehiclesCount) || 0) - (Number(bid.acceptedVehiclesCount) || 0));
      const nextCount = Math.max(0, Math.min(availableByBid, currentCount + delta));
      const currentTotalWithoutBid = allocatedVehicles - currentCount;
      const limitedCount = Math.min(nextCount, Math.max(0, totalVehicles - currentTotalWithoutBid));
      return {
        ...prev,
        [bid.id]: {
          vehiclesCount: limitedCount,
          vehiclesDates: buildInitialDates(limitedCount, currentItem.vehiclesDates.map((shippingDate) => ({ shippingDate }))),
        },
      };
    });
  };

  const setBidDates = (bidId, dates) => {
    setDistribution((prev) => ({
      ...prev,
      [bidId]: {
        ...(prev[bidId] || {}),
        vehiclesDates: dates,
      },
    }));
  };

  const changeBidDate = (bidId, index, date) => {
    setDistribution((prev) => {
      const currentItem = prev[bidId] || { vehiclesCount: 0, vehiclesDates: [] };
      return {
        ...prev,
        [bidId]: {
          ...currentItem,
          vehiclesDates: (currentItem.vehiclesDates || []).map((item, itemIndex) => (
            itemIndex === index ? date : item
          )),
        },
      };
    });
  };

  const handleDatePanelClick = (item, selected) => {
    if ((Number(selected?.vehiclesCount) || 0) > 9) {
      setInlineDateBidId(null);
      setDateModalBidId(item.id);
      return;
    }

    setDateModalBidId(null);
    setInlineDateBidId((prev) => (prev === item.id ? null : item.id));
  };

  const buildPayload = useCallback(() => (
    normalizedItems.reduce((acc, item) => {
      const selected = distribution[item.id];
      const vehiclesCount = Number(selected?.vehiclesCount) || 0;
      if (!vehiclesCount) return acc;

      const vehiclesDates = selected.vehiclesDates || [];
      const hasEmptyDate = vehiclesDates.length !== vehiclesCount || vehiclesDates.some((date) => !date);
      if (hasEmptyDate) {
        throw new Error("dates_required");
      }

      acc.push({
        id: item.id,
        expeditorId: item.expeditorId,
        vehiclesCount,
        price: item.price,
        currency: item.currency,
        vehiclesDates: vehiclesDates.map((shippingDate) => ({
          shippingDate: moment.utc(shippingDate).startOf("day").toISOString(),
        })),
      });
      return acc;
    }, [])
  ), [normalizedItems, distribution]);

  const confirmDistribution = async () => {
    setIsSubmitting(true);
    try {
      const payload = buildPayload();

      if (payload.length === 0) {
        toast.error(t("complexTender.clientBids.selectCarriersError"));
        return;
      }

      const isFullyDistributed = totalVehicles > 0 && allocatedVehicles >= totalVehicles;

      await Order.confirmComplexOrderDistribution(order.id, payload);
      toast.success(t("complexTender.clientBids.distributionSuccess"));

      await delay(ORDERS_CREATION_DELAY_MS);
      if (!isMountedRef.current) return;

      if (isFullyDistributed) {
        onUpdateData?.();
        onClose?.();
        return;
      }

      await fetchOrderData?.();
      onUpdateData?.();
      setIsDistributionMode(false);
    } catch (error) {
      if (error?.message === "dates_required") {
        toast.error(t("complexTender.clientBids.datesRequiredError", { context: transportContext }));
      } else {
        toast.error(t("complexTender.clientBids.distributionError"));
      }
    } finally {
      if (isMountedRef.current) setIsSubmitting(false);
    }
  };

  const activeModalBid = normalizedItems.find((item) => item.id === dateModalBidId);
  const activeModalDistribution = activeModalBid ? distribution[activeModalBid.id] : null;

  return (
    <div className={style.card}>
      <div className={style.topbar}>
        <span className={style.title}>
          {t(titleKey)}:{" "}
          <span>
            {totalVehicles} {vehicleWord(totalVehicles)}
          </span>
        </span>
        {order?.tenderDateEnd && (
          <span className={style.deadline}><TenderDeadlineText date={order.tenderDateEnd} /></span>
        )}
      </div>

      {order?.isInRebid === true && isTenderTimeReady && !isTenderEnded && (
        <RebidNotice>{t("orderInfoClient.rebid.clientNotice")}</RebidNotice>
      )}

      <div className={cn(style.head_row, { [style.head_row_distribution]: isDistributionMode })}>
        <span>{t("complexTender.clientBids.carrier")}</span>
        <span>{t("complexTender.clientBids.date")}</span>
        {!isDistributionMode && <span>{t("complexTender.clientBids.amount")}</span>}
        <span>{t("complexTender.clientBids.deliveryTerm")}</span>
        {isDistributionMode ? (
          <span>{t("complexTender.clientBids.price")}</span>
        ) : (
          <span>{t("complexTender.clientBids.available")}</span>
        )}
        {isDistributionMode && <span>{t("complexTender.clientBids.assign")}</span>}
        <span />
      </div>

      {normalizedItems.length === 0 && (
        <div className={style.empty}>
          <span className={style.empty_text}>{t("orderInfo.suggestPrice.noOffersYet")}</span>
          <ThreeDots />
        </div>
      )}

      {normalizedItems.map((item) => {
        const currencySymbol = CURRENCIES[item?.currency || "KZT"]?.symbol || item?.currency || "";
        const hasComment = Boolean(item?.comments);
        const isCommentOpen = activeCommentId === item.id;
        const selected = distribution[item.id] || { vehiclesCount: 0, vehiclesDates: [] };
        const selectedDatesCount = (selected.vehiclesDates || []).filter(Boolean).length;
        const isInlineDateOpen = inlineDateBidId === item.id && selected.vehiclesCount <= 9;
        const availableCount = Math.max(0, (Number(item.vehiclesCount) || 0) - (Number(item.acceptedVehiclesCount) || 0));
        const bidName = item.expeditorName || item.userName || EMPTY_VALUE;
        const bidNameTooltipId = `complex-tender-bid-name-tooltip-${item.id || index}`;

        return (
          <React.Fragment key={item.id}>
            <div className={cn(style.row, { [style.row_distribution]: isDistributionMode })}>
              <span className={style.cell_name} data-tooltip-id={bidNameTooltipId}>
                {bidName}
              </span>
              <span className={style.cell}>{formatBidDate(item.updatedAt)}</span>
              {!isDistributionMode && (
                <span className={style.cell_amount}>{numberToFinanceFormat(item.price)} {currencySymbol}</span>
              )}
              <span className={style.cell}>
                {item?.plannedDays ? t("complexTender.units.days", { count: item.plannedDays }) : EMPTY_VALUE}
              </span>
              {isDistributionMode ? (
                <span className={style.cell_amount}>{numberToFinanceFormat(item.price)} {currencySymbol}</span>
              ) : (
                <div className={style.available_cell}>
                  <span className={style.cell}>
                    {availableCount} {vehicleWord(availableCount)}
                  </span>
                  {item.acceptedVehiclesCount ? (
                    <span className={style.cell}>
                      {t("complexTender.clientBids.allocatedCount", { count: item.acceptedVehiclesCount })}
                    </span>
                  ) : null}
                </div>
              )}
              {isDistributionMode && (
                <div className={style.stepper}>
                  <button onClick={() => changeVehiclesCount(item, -1)} disabled={!selected.vehiclesCount}>-</button>
                  <span>{selected.vehiclesCount}</span>
                  <button onClick={() => changeVehiclesCount(item, 1)} disabled={selected.vehiclesCount >= availableCount || allocatedVehicles >= totalVehicles}>+</button>
                </div>
              )}
              <div className={style.row_actions}>
                {hasComment && (
                  <button
                    className={style.comment_btn}
                    onClick={() => setActiveCommentId(isCommentOpen ? null : item.id)}
                    title={t("complexTender.clientBids.carrierComment")}
                  >
                    <CommentIcon type={3} fill="#0066ff" />
                  </button>
                )}
              </div>
            </div>
            <AppTooltip id={bidNameTooltipId} place="top" positionStrategy="fixed">
              {bidName}
            </AppTooltip>

            {isCommentOpen && hasComment && (
              <div className={style.comment_box}>
                <div className={style.comment_box_head}>{t("complexTender.clientBids.carrierComment")}</div>
                <div className={style.comment_box_body}>{item.comments}</div>
              </div>
            )}

            {isDistributionMode && selected.vehiclesCount > 0 && (
              <div className={style.date_panel_group}>
                <button
                  className={cn(style.date_panel, { [style.date_panel_open]: isInlineDateOpen })}
                  onClick={() => handleDatePanelClick(item, selected)}
                >
                  <span>{t("complexTender.clientBids.loadingDate")}</span>
                  <span>
                    {selectedDatesCount
                      ? `${selectedDatesCount} ${vehicleWord(selectedDatesCount)}: ${formatDate(selected.vehiclesDates[0])}`
                      : t("complexTender.clientBids.chooseLoadingDate")}{" "}
                    <ArrowDownIconNew />
                  </span>
                </button>

                {isInlineDateOpen && (
                  <div className={style.inline_date_panel}>
                    <DateFields
                      dates={selected.vehiclesDates || []}
                      transportContext={transportContext}
                      onChangeDate={(index, date) => changeBidDate(item.id, index, date)}
                    />
                  </div>
                )}
              </div>
            )}
          </React.Fragment>
        );
      })}

      {normalizedItems.length > 0 && (
        <div className={style.footer}>
          {isDistributionMode ? (
            <>
              <span className={style.distribution_text}>
                {t("complexTender.clientBids.distributed")}:{" "}
                <b>{t("complexTender.common.countOfTotal", { count: allocatedVehicles, total: totalVehicles })}</b>
              </span>
              <div className={style.footer_buttons}>
                <CustomButton
                  text={t("complexTender.clientBids.reset")}
                  isNavy
                  isWhite
                  onClick={resetDistribution}
                  disabled={isSubmitting}
                />
                <CustomButton
                  text={t("complexTender.clientBids.confirmAndCreate")}
                  isNavy
                  onClick={confirmDistribution}
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                />
              </div>
            </>
          ) : (
            <>
              {acceptedVehicles > 0 && (
                <span className={style.distribution_text}>
                  {t("complexTender.clientBids.distributed")}:{" "}
                  <b>{t("complexTender.common.countOfTotal", { count: acceptedVehicles, total: totalVehicles })}</b>
                </span>
              )}
              <CustomButton
                text={t(
                  isAuction
                    ? "complexTender.clientBids.finishAndDistributeAuction"
                    : "complexTender.clientBids.finishAndDistribute",
                )}
                isNavy
                onClick={startDistribution}
              />
            </>
          )}
        </div>
      )}

      <DateDistributionModal
        isOpen={Boolean(activeModalBid)}
        transportContext={transportContext}
        bid={activeModalBid}
        count={Number(activeModalDistribution?.vehiclesCount) || 0}
        dates={activeModalDistribution?.vehiclesDates || []}
        onChangeDates={(dates) => setBidDates(activeModalBid.id, dates)}
        onClose={() => setDateModalBidId(null)}
      />
    </div>
  );
};

export default ComplexTenderBids;
